import React, { useState } from "react";
import {
	useFormContext,
	useFormState,
	ValidationRule,
	Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { get } from "lodash";

import classNames from "classnames";

import PopoverMessage from "./PopoverMessage";
import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import { FormInputFieldProps } from "./types";

export default function FormInputField(mainProps: FormInputFieldProps) {
	const { pattern, validate, ...props } = mainProps;

	const {
		// Element type
		type = "text",

		// Identifiers
		id = undefined,
		name,
		label,

		// Validation & behavior
		required = false,
		popoverMsg = null,

		// Input-specific props
		placeholder = undefined,

		// Styling
		className,
		labelWrapperClassName = undefined,
		labelClassName = undefined,
		asterisksClass = undefined,
		inputWrapperClassName = undefined,
		inputClassName = undefined,
		validationClassName = undefined,

		// Data / content
		children,

		// Modifiers
		isInline = false,

		// Remaining props
		...rest
	} = props;

	const [showPopoverMsg, setShowPopoverMsg] = useState(false);

	const { t } = useTranslation();
	const { register } = useFormContext();

	const { errors } = useFormState();
	const error = get(errors, name);

	const field = register(name, {
		required: {
			value: required === true,
			message: props.errorMessage || "COMMON.REQUIRED_FIELD",
		},
		validate,
		pattern,
	});

	const labelClasses = classNames("label", labelClassName);

	const inputClass = classNames(inputClassName, {
		"input input--base":
			inputClassName == undefined || inputClassName == null,
		"input-validation-error": error != undefined,
	});

	if (label == undefined || className == undefined) {
		return (
			<input
				type={type}
				id={id}
				placeholder={t(placeholder || "")}
				className={inputClassName}
				{...field}
				{...rest}
			/>
		);
	}

	if (label != undefined && className != undefined && isInline) {
		return (
			<div className={className || "form__field"}>
				<div className={labelWrapperClassName || "form__field__left"}>
					<label
						htmlFor={id}
						className={labelClassName || "label"}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setShowPopoverMsg(true);
						}}
					>
						{t<string>(label)}

						<RequiredMark
							required={required}
							asterisksClass={asterisksClass}
						/>

						<PopoverMessage
							showPopoverMsg={showPopoverMsg}
							setShowPopoverMsg={setShowPopoverMsg}
							popoverMsg={popoverMsg}
						/>
					</label>
				</div>

				<div className={inputWrapperClassName || "form__field__right"}>
					<input
						type={type}
						id={id}
						placeholder={t<string>(placeholder || "")}
						className={inputClassName || "input input--base"}
						{...field}
						{...rest}
					/>

					<ErrorMessage
						name={name}
						addClassName={validationClassName}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={className}>
			<label
				htmlFor={id}
				className={labelClasses}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setShowPopoverMsg(true);
				}}
			>
				{t<string>(label)}
				<RequiredMark
					required={required}
					asterisksClass={asterisksClass}
				/>

				<PopoverMessage
					showPopoverMsg={showPopoverMsg}
					setShowPopoverMsg={setShowPopoverMsg}
					popoverMsg={popoverMsg}
				/>
			</label>

			<ErrorMessage name={name} addClassName={validationClassName} />

			<input
				type={type}
				id={id}
				placeholder={t<string>(placeholder || "")}
				className={inputClass}
				{...field}
				{...rest}
			/>
			{children}
		</div>
	);
}
